import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';

const POLL_MS = 30000;

// State + actions for "La mia selezione" (imported betslip followed live).
export const useMySelection = (enabled, polling, onLimit) => {
  const [selection, setSelection] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [importing, setImporting] = useState(false);
  const timer = useRef(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await axios.get(`${API}/my-selection`);
      setSelection(data?.selection || null);
    } catch { /* keep last state */ }
    setLoaded(true);
  }, [enabled]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!enabled || !polling) return undefined;
    timer.current = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer.current);
  }, [enabled, polling, refresh]);

  const importFile = async (file) => {
    if (!file) return false;
    if (file.size > 10 * 1024 * 1024) { toast.error('Immagine troppo grande (max 10 MB).'); return false; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const { data } = await axios.post(`${API}/my-selection/import`, fd, { timeout: 90000 });
      setSelection(data?.selection || null);
      const n = data?.selection?.picks?.length || 0;
      toast.success(`Selezione importata: ${n} ${n === 1 ? 'partita' : 'partite'}`);
      return true;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'basic_limit' && onLimit) onLimit();
      else toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Import non riuscito. Riprova.');
      return false;
    } finally {
      setImporting(false);
    }
  };

  const remove = async () => {
    try { await axios.delete(`${API}/my-selection`); } catch { /* noop */ }
    setSelection(null);
  };

  const removePick = async (fixtureId) => {
    try {
      const { data } = await axios.delete(`${API}/my-selection/pick/${fixtureId}`);
      setSelection(data?.selection || null);
      return data?.selection || null;
    } catch {
      toast.error('Eliminazione non riuscita. Riprova.');
      return selection;
    }
  };

  const addPick = async (fixtureId, market) => {
    try {
      const { data } = await axios.post(`${API}/my-selection/pick`, { fixture_id: fixtureId, market });
      setSelection(data?.selection || null);
      toast.success('Partita aggiunta alla tua selezione');
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Aggiunta non riuscita. Riprova.');
      return false;
    }
  };

  const setNotify = async (enabled) => {
    try {
      const { data } = await axios.post(`${API}/my-selection/notify`, { enabled });
      setSelection(data?.selection || null);
      if (enabled) toast.success('Avvisi attivi per questa selezione');
    } catch {
      toast.error('Impostazione avvisi non riuscita. Riprova.');
    }
  };

  return { selection, loaded, importing, refresh, importFile, remove, removePick, addPick, setNotify };
};

export default useMySelection;
